import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import SidebarHeader from '../SidebarHeader/SidebarHeader';
import { useAuth } from '../../context/AuthContext';
import { useSettings } from '../../context/SettingsContext';
import hiveService from '../../services/hive';
import './CommunityList.css';

const toCanonicalCommunityKey = (communityLike) => {
  const raw = String(communityLike || '').trim().toLowerCase();
  if (!raw) return '';
  return raw.startsWith('hive-') ? raw : `hive-${raw}`;
};

const CommunityList = ({ onOpenLeftSidebar, onOpenRightSidebar }) => {
  const { user, loading: authLoading } = useAuth();
  const { communitiesViewMode, updateCommunitiesViewMode, saveSettings, autoload_more = true } = useSettings();
  const [communities, setCommunities] = useState([]);
  const { tab } = useParams();
  const navigate = useNavigate();

  // Redirect /communities to the right default tab once auth is resolved
  useEffect(() => {
    if (tab || authLoading) return;
    navigate(user ? '/communities/mine' : '/communities/rank', { replace: true });
  }, [tab, authLoading, user, navigate]);

  const activeTab = tab || (user ? 'mine' : 'rank');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [limit, setLimit] = useState(20);

  const [searchQuery, setSearchQuery] = useState('');
  const [activeSearch, setActiveSearch] = useState('');

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setActiveSearch(searchQuery.trim().length >= 2 ? searchQuery.trim() : '');
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Reset limit and search when tab changes
  useEffect(() => {
    setLimit(20);
    setAutoloadUnlocked(false);
    setSearchQuery('');
    setActiveSearch('');
  }, [activeTab]);

  // Autoload Observer State
  const [autoloadUnlocked, setAutoloadUnlocked] = useState(false);
  const loadMoreObserverRef = useRef(null);

  // Initial trigger unlocks autoload once communities are populated
  useEffect(() => {
    if (!autoload_more) return;
    if (communities.length > 0) {
      setAutoloadUnlocked(true);
    }
  }, [communities.length, autoload_more]);

  // Observer callback for autoloading
  useEffect(() => {
    const hasMore = communities.length === limit;
    if (!autoload_more || !autoloadUnlocked || !hasMore || loading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setLimit(prev => prev + 20);
        }
      },
      { rootMargin: '400px' }
    );

    const target = loadMoreObserverRef.current;
    if (target) observer.observe(target);

    return () => {
      if (target) observer.unobserve(target);
    };
  }, [autoload_more, autoloadUnlocked, communities.length, limit, loading]);


  // Toggle for the display settings panel (grid/list selection)
  const [displaySettingsOpen, setDisplaySettingsOpen] = useState(false);

  const viewMode = communitiesViewMode || 'list';

  // Subscription state: Set of community names the logged-in user subscribes to
  const [subscribedNames, setSubscribedNames] = useState(new Set());
  const [subscriptionsLoading, setSubscriptionsLoading] = useState(false);
  // IDs currently being toggled (to show loading state per-button)
  const [togglingIds, setTogglingIds] = useState(new Set());

  const handleViewModeChange = (mode) => {
    updateCommunitiesViewMode(mode);
    saveSettings({ communitiesViewMode: mode });
  };

  // Load the user's subscribed community names (for non-mine tabs)
  useEffect(() => {
    if (!user?.username) {
      setSubscribedNames(new Set());
      setSubscriptionsLoading(false);
      return;
    }
    setSubscriptionsLoading(true);
    fetch(`/api/feed/communities/mine?username=${encodeURIComponent(user.username)}&limit=1000`)
      .then(r => r.json())
      .then(data => {
        const names = new Set(
          (data.communities || [])
            .map(c => toCanonicalCommunityKey(c.id || c.name))
            .filter(Boolean)
        );
        setSubscribedNames(names);
      })
      .catch(() => {})
      .finally(() => setSubscriptionsLoading(false));
  }, [user?.username]);

  const handleToggleSubscription = async (e, community) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user?.username) return;
    const canonicalKey = toCanonicalCommunityKey(community.id || community.name);
    const communityNameForChain = String(community.id || community.name || '').trim();
    const isSubscribed = subscribedNames.has(canonicalKey);

    if (!canonicalKey || !communityNameForChain) return;

    // Optimistic update
    setSubscribedNames(prev => {
      const next = new Set(prev);
      isSubscribed ? next.delete(canonicalKey) : next.add(canonicalKey);
      return next;
    });
    // On 'mine' tab, also remove from list immediately
    if (activeTab === 'mine' && isSubscribed) {
      setCommunities(prev => prev.filter(c => toCanonicalCommunityKey(c.id || c.name) !== canonicalKey));
    }

    setTogglingIds(prev => new Set(prev).add(canonicalKey));
    try {
      if (isSubscribed) {
        await hiveService.unsubscribeCommunity(user.username, communityNameForChain);
      } else {
        await hiveService.subscribeCommunity(user.username, communityNameForChain);
      }
    } catch (err) {
      // Roll back optimistic update on error
      setSubscribedNames(prev => {
        const next = new Set(prev);
        isSubscribed ? next.add(canonicalKey) : next.delete(canonicalKey);
        return next;
      });
      if (activeTab === 'mine' && isSubscribed) {
        setCommunities(prev => [...prev, community]);
      }
      alert(err.message || 'Failed to update subscription');
    } finally {
      setTogglingIds(prev => { const next = new Set(prev); next.delete(canonicalKey); return next; });
    }
  };

  const loadCommunities = useCallback(async () => {
    try {
      if (limit === 20) setLoading(true);
      else setLoadingMore(true);

      if (activeTab === 'mine') {
        if (!user?.username) {
          setCommunities([]);
          return;
        }

        // Sync on-chain subscriptions first (fast, non-blocking on UI since we await)
        try {
          const syncResp = await fetch(`/api/feed/communities/mine/sync?username=${encodeURIComponent(user.username)}`);
          if (syncResp.ok) {
            const syncData = await syncResp.json();
            if (syncData.changed) {
              console.log('[CommunityList] Synced on-chain subscriptions, changes detected');
            }
          }
        } catch (syncErr) {
          console.warn('[CommunityList] Subscription sync failed:', syncErr.message);
        }

        let url = `/api/feed/communities/mine?username=${encodeURIComponent(user.username)}&limit=${limit}`;
        if (activeSearch) url += `&search=${encodeURIComponent(activeSearch)}`;
        const response = await fetch(url);
        const data = await response.json();
        setCommunities(data.communities || []);
        return;
      }

      let url = `/api/feed/communities?sort=${activeTab}&limit=${limit}`;
      if (activeSearch) url += `&search=${encodeURIComponent(activeSearch)}`;
      const response = await fetch(url);
      const data = await response.json();
      setCommunities(data.communities || []);
    } catch (error) {
      console.error('Error loading communities:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [activeTab, limit, user?.username, activeSearch]);

  useEffect(() => {
    loadCommunities();
  }, [loadCommunities]);

  const formatNumber = (num) => {
    const n = Number(num) || 0;
    if (n >= 1000000) {
      return (n / 1000000).toFixed(1) + 'M';
    }
    if (n >= 1000) {
      return (n / 1000).toFixed(1) + 'K';
    }
    return n.toString();
  };

  // Pluralize helper: singular when exactly 1, otherwise plural (including 0)
  const pluralize = (count, singular, plural) => (Number(count) === 1 ? singular : plural);



  return (
    <>
      <SidebarHeader title="Communities" onOpenLeftSidebar={onOpenLeftSidebar} onOpenRightSidebar={onOpenRightSidebar} />
      <div className="community-list-container">
      {/* Header */}
      <div className="communities-header">
        <h1 className="communities-title">Communities</h1>
        <div className="communities-controls">
          <div className="sort-controls">
            <select
              className="sort-dropdown"
              value={activeTab}
              onChange={(e) => navigate('/communities/' + e.target.value)}
            >
              <option value="rank">Popular</option>
              <option value="new">New</option>
              <option value="subs">Most Members</option>
              <option value="mine" disabled={!user?.username}>My Communities</option>
            </select>
            <button
              className={`sort-btn ${activeTab === 'rank' ? 'active' : ''}`}
              onClick={() => navigate('/communities/rank')}
            >
              Popular
            </button>
            <button
              className={`sort-btn ${activeTab === 'new' ? 'active' : ''}`}
              onClick={() => navigate('/communities/new')}
            >
              New
            </button>
            <button
              className={`sort-btn ${activeTab === 'subs' ? 'active' : ''}`}
              onClick={() => navigate('/communities/subs')}
            >
              Most Members
            </button>
            <button
              className={`sort-btn ${activeTab === 'mine' ? 'active' : ''}`}
              onClick={() => navigate('/communities/mine')}
              disabled={!user?.username}
            >
              My Communities
            </button>

            {/* Display settings toggle (appears inline with sort controls) */}
            <button
              className={`display-settings-btn ${displaySettingsOpen ? 'open' : ''}`}
              onClick={() => setDisplaySettingsOpen(prev => !prev)}
              aria-expanded={displaySettingsOpen}
            >
              <span className="display-label">Display</span>
              <span className={`arrow ${displaySettingsOpen ? 'open' : ''}`}>▾</span>
            </button>
          </div>
          
          <div className="community-search-container">
            <input
              type="text"
              className="community-search-input"
              placeholder="Search communities..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          {/* Display settings panel - toggled by the Display button */}
          {displaySettingsOpen && (
            <div className="view-controls layout-selector">
              <div className="flux-grid">
                <button
                  title='Grid view'
                  className={`layout-card ${viewMode === 'grid' ? 'active' : ''}`}
                  onClick={() => { handleViewModeChange('grid'); setDisplaySettingsOpen(false); }}
                >
                  <div className="flux-preview grid-preview preview-small">
                    <div className="flux-tile"></div>
                    <div className="flux-tile"></div>
                    <div className="flux-tile"></div>
                    <div className="flux-tile"></div>
                    <div className="flux-tile"></div>
                    <div className="flux-tile"></div>
                  </div>
                </button>

                <button
                  title='List view'
                  className={`layout-card ${viewMode === 'list' ? 'active' : ''}`}
                  onClick={() => { handleViewModeChange('list'); setDisplaySettingsOpen(false); }}
                >
                  <div className="flux-preview list-preview preview-small">
                    <div className="flux-row"></div>
                    <div className="flux-row"></div>
                    <div className="flux-row"></div>
                  </div>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {activeTab === 'mine' && !user?.username && (
        <div className="communities-empty">
          <p>Login to view your communities.</p>
        </div>
      )}

      {activeTab === 'mine' && user?.username && communities.length === 0 && !loading && (
        <div className="communities-empty">
          <p>You are not following any communities yet.</p>
        </div>
      )}

      {loading ? (
        <div className="loading-container" style={{ marginTop: '2rem' }}>
          <LoadingSpinner />
        </div>
      ) : (
        <div className={`communities-grid ${viewMode === 'list' ? 'list-view' : ''}`}>
          {communities.map((community) => {
              const communityKey = toCanonicalCommunityKey(community.id || community.name);
              const isSubscribed = subscribedNames.has(communityKey);
              const isButtonBusy = subscriptionsLoading || togglingIds.has(communityKey);
              return (
            <Link
              key={community.name}
              to={`/c/${community.id || community.name}`}
              className={`community-card ${viewMode === 'list' ? 'list-view' : ''}`}
            >
              <div className="community-card-header">
                <h3 className="community-card-name">{community.title}</h3>
                {community.is_nsfw && (
                  <span className="nsfw-badge">NSFW</span>
                )}
              </div>

              {community.title && (
                <p className="community-card-title">c/{community.id}</p>
              )}

              {community.about && (
                <p className="community-card-description">
                  {community.about.length > 120 
                    ? community.about.substring(0, 120) + '...' 
                    : community.about}
                </p>
              )}

              <div className="community-card-stats">
                <div className="stat">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                  </svg>
                  <span>{formatNumber(community.subscribers)} {pluralize(community.subscribers, 'member', 'members')}</span>
                </div>
                {(() => {
                  const pendingCount = Number(community.num_pending) || 0;
                  const authorsCount = Number(community.num_authors) || 0;
                  
                  return (pendingCount > 0 || authorsCount > 0) ? (
                    <div className="stat">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
                      </svg>
                      <span>
                        {pendingCount > 0 && authorsCount > 0 
                          ? `${formatNumber(pendingCount)} ${pluralize(pendingCount, 'post', 'posts')} by ${formatNumber(authorsCount)} ${pluralize(authorsCount, 'author', 'authors')}`
                          : pendingCount > 0 
                            ? `${formatNumber(pendingCount)} active ${pluralize(pendingCount, 'post', 'posts')}`
                            : `${formatNumber(authorsCount)} active ${pluralize(authorsCount, 'author', 'authors')}`
                        }
                      </span>
                    </div>
                  ) : null;
                })()}
              </div>

            {community.team && community.team.length > 0 && (
              <div className="community-card-admins">
                <span className="admins-label">Admins:</span>
                <span className="admins-list">
                  {community.team.slice(0, 3).map(admin => `@${admin[0]}`).join(', ')}
                  {community.team.length > 3 && ` +${community.team.length - 3} more`}
                </span>
              </div>
            )}

            {user?.username && (
              <button
                className={`community-sub-btn ${isSubscribed ? 'subscribed' : ''}`}
                onClick={(e) => handleToggleSubscription(e, community)}
                disabled={isButtonBusy}
              >
                {isButtonBusy && <span className="community-sub-btn-spinner" aria-hidden="true" />}
                <span>{isSubscribed ? 'Unsubscribe' : 'Subscribe'}</span>
              </button>
            )}
          </Link>
              );
          })}
        </div>
      )}

      {/* Load More */}
      {communities.length === limit && (
        <div className="load-more-container" ref={autoload_more ? loadMoreObserverRef : null}>
          {loadingMore ? (
            <LoadingSpinner />
          ) : (!autoload_more || !autoloadUnlocked) && (
            <button
              className="load-more-communities-btn"
              onClick={() => setLimit(limit + 20)}
            >
              Load More Communities
            </button>
          )}
        </div>
      )}
    </div>
    </>
  );
};

export default CommunityList;
